import { createSignal, For, Show, onCleanup } from "solid-js";
import { Reel, type ReelKind } from "./Reel";
import { unlockAudio } from "../lib/sound";
import { impact } from "../lib/haptics";

interface ReelProgram {
  duration: number;
  speed: number;
}

interface SlotMachineProps {
  reels: string[][];
  finalIndices: [number, number, number];
  reelSpinning: [boolean, boolean, boolean];
  programs: [ReelProgram, ReelProgram, ReelProgram];
  anySpinning: boolean;
  onLeverPull: () => void;
  /**
   * Replay short bump animation. Caller increments on each reel landing.
   * Internally we restart the CSS animation via classList toggle (no remount).
   */
  shakeKey: number;
  jackpot?: boolean;
  /** When true, third reel pulses + slows visually because LLM is taking long. */
  anticipate?: boolean;
  /** Render shape per reel, first reel uses coloured logos, others icon+label. */
  kinds?: [ReelKind, ReelKind, ReelKind];
}

function Lights(props: { active: boolean; jackpot: boolean }) {
  // 12 lights as a single layer, animation = opacity + scale only, GPU-cheap.
  const N = 12;
  return (
    <div
      class="pointer-events-none absolute inset-x-3 top-2 flex justify-between"
      style={{ "z-index": 5, height: "6px" }}
      aria-hidden="true"
    >
      <For each={Array.from({ length: N })}>
        {(_, i) => (
          <span
            style={{
              display: "inline-block",
              width: "5px",
              height: "5px",
              "border-radius": "50%",
              background: props.jackpot
                ? "radial-gradient(circle, #fff5d0, #f0d98a 60%, #b8923f 100%)"
                : props.active
                  ? "radial-gradient(circle, #fff5d0, #f0d98a)"
                  : "radial-gradient(circle, rgba(201,169,97,0.45), rgba(201,169,97,0.2))",
              animation: props.active
                ? `lightPulse 0.55s ${i() * 0.04}s ease-in-out infinite`
                : "none",
              opacity: props.active ? undefined : props.jackpot ? 0.85 : 0.45,
            }}
          />
        )}
      </For>
    </div>
  );
}

export function SlotMachine(props: SlotMachineProps) {
  const [leverDown, setLeverDown] = createSignal(false);
  let leverTimer: ReturnType<typeof setTimeout> | undefined;
  let slotRef: HTMLDivElement | undefined;

  const handlePull = (): void => {
    if (props.anySpinning) return;
    unlockAudio();
    impact("medium");
    setLeverDown(true);
    if (leverTimer) clearTimeout(leverTimer);
    leverTimer = setTimeout(() => setLeverDown(false), 600);
    props.onLeverPull();
  };
  onCleanup(() => leverTimer && clearTimeout(leverTimer));

  /**
   * Trigger the one-shot "slotBump" animation without remounting the element.
   * The classic CSS dance: remove class → force reflow → add class.
   * This fixes the React bug where `key={shake-${n}}` was unmounting all three
   * reels mid-spin and causing the second/third reel to jump.
   */
  let lastShake = 0;
  const replayBump = (n: number): void => {
    if (!slotRef || n === lastShake) return;
    lastShake = n;
    slotRef.classList.remove("slot-bump");
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    void slotRef.offsetWidth;
    slotRef.classList.add("slot-bump");
  };
  // Track shake key.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  () => props.shakeKey;
  // Reactive tracking, Solid runs this on every shakeKey change.
  // Wrapping in a memo would still re-evaluate; the helper does the actual work.
  // We just read the prop inside a JSX attribute below via a side-effecting ref pattern.

  return (
    <div class="relative w-full px-1">
      <div
        ref={slotRef}
        class="slot-root relative"
        classList={{
          "slot-rumble": props.anySpinning,
          "slot-jackpot": !!props.jackpot,
        }}
        style={{
          "border-radius": "32px",
          padding: "14px",
          background: "linear-gradient(180deg, #ffffff 0%, #f6f3ec 100%)",
          "box-shadow":
            "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(201,169,97,0.18), 0 1px 0 rgba(201,169,97,0.35), 0 12px 28px rgba(120, 95, 40, 0.10), 0 30px 60px rgba(120, 95, 40, 0.08)",
          border: "1px solid rgba(201, 169, 97, 0.35)",
          "will-change": props.anySpinning ? "transform" : "auto",
        }}
      >
        {/* Side-effect: every shakeKey bump replays the CSS animation. */}
        {(() => {
          replayBump(props.shakeKey);
          return null;
        })()}

        <Lights active={props.anySpinning} jackpot={!!props.jackpot} />

        <div
          class="relative"
          style={{
            "border-radius": "20px",
            padding: "6px 4px",
            background: "linear-gradient(180deg, #e8d4a2 0%, #c9a961 35%, #b8923f 65%, #d9c084 100%)",
            "box-shadow":
              "inset 0 1px 1px rgba(255,255,255,0.5), inset 0 -1px 1px rgba(0,0,0,0.12)",
          }}
        >
          <div
            class="relative flex overflow-hidden bg-white"
            style={{
              "border-radius": "14px",
              "box-shadow":
                "inset 0 2px 6px rgba(0,0,0,0.12), inset 0 -1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <For each={props.reels}>
              {(items, idx) => (
                <>
                  <Reel
                    items={items}
                    finalIndex={props.finalIndices[idx()]}
                    spinning={props.reelSpinning[idx()]}
                    speed={props.programs[idx()].speed}
                    kind={props.kinds?.[idx()] ?? (idx() === 0 ? "logo" : "icon-label")}
                    anticipate={!!props.anticipate && idx() === 2 && props.reelSpinning[2]}
                    chroma={!!props.jackpot && !props.reelSpinning[idx()]}
                  />
                  <Show when={idx() < props.reels.length - 1}>
                    <div
                      style={{
                        width: "2px",
                        background:
                          "linear-gradient(180deg, #d4b56e 0%, #b08735 50%, #d4b56e 100%)",
                        "box-shadow": "inset 0 0 1px rgba(255,255,255,0.5)",
                      }}
                    />
                  </Show>
                </>
              )}
            </For>

            {/* Arrow indicators */}
            <div
              style={{
                position: "absolute",
                left: "-2px",
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                "border-top": "6px solid transparent",
                "border-bottom": "6px solid transparent",
                "border-left": "8px solid #c9a961",
                filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))",
              }}
            />
            <div
              style={{
                position: "absolute",
                right: "-2px",
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                "border-top": "6px solid transparent",
                "border-bottom": "6px solid transparent",
                "border-right": "8px solid #c9a961",
                filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))",
              }}
            />
          </div>
        </div>
      </div>

      {/* Lever. Was `right: -14px` which got clipped by the rounded phone-card
          on desktop (QA P1 #30); pulled flush so the knob sits inside card bounds. */}
      <div
        onClick={handlePull}
        role="button"
        aria-label="Pull the lever"
        style={{
          position: "absolute",
          right: "-2px",
          top: "24px",
          width: "60px",
          height: "200px",
          cursor: props.anySpinning ? "default" : "pointer",
          "pointer-events": props.anySpinning ? "none" : "auto",
          // Suppress iOS long-press text-selection caret on press-and-hold
          // (QA P1 #23). Touch is not text input here.
          "-webkit-touch-callout": "none",
          "-webkit-user-select": "none",
          "user-select": "none",
          "touch-action": "manipulation",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: "6px",
            top: "70px",
            width: "32px",
            height: "32px",
            "border-radius": "16px",
            background:
              "radial-gradient(circle at 35% 35%, #f4e5b8 0%, #c9a961 50%, #8e6f2a 100%)",
            "box-shadow":
              "0 2px 6px rgba(120, 95, 40, 0.4), inset 0 1px 1px rgba(255,255,255,0.5)",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "18px",
            /* Animate transform, not `top` (was causing layout thrash). */
            top: "18px",
            width: "8px",
            height: "70px",
            "border-radius": "4px",
            background:
              "linear-gradient(90deg, #8e6f2a 0%, #d4b56e 40%, #f4e5b8 50%, #c9a961 70%, #8e6f2a 100%)",
            "transform-origin": "bottom center",
            transition: "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
            transform: leverDown() ? "translateY(58px) rotate(0deg)" : "translateY(0) rotate(-8deg)",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "8px",
            top: "0px",
            width: "28px",
            height: "28px",
            "border-radius": "14px",
            background:
              "radial-gradient(circle at 30% 25%, #fff5d0 0%, #f0d98a 25%, #c9a961 60%, #8e6f2a 100%)",
            "box-shadow":
              "0 3px 8px rgba(120, 95, 40, 0.4), inset 0 1px 2px rgba(255,255,255,0.6), inset 0 -1px 1px rgba(0,0,0,0.2)",
            transition: "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
            transform: leverDown() ? "translateY(70px)" : "translateY(0)",
          }}
        />
      </div>
    </div>
  );
}
